/**
 * a11ySettings — состояние гранулярной панели accessibility (Фаза 9, П2).
 *
 * Многослойная архитектура (см. `plans/phase9-ui-ux-improvements.md` §П2):
 *  - **Слой 0 (native baseline, без JS):** CSS `@media (prefers-reduced-motion)` /
 *    `prefers-contrast: more` / `forced-colors: active` в `index.css` реагируют на
 *    системные настройки ОС бесплатно — работают до рендера React.
 *  - **Слой 1 (in-app панель, этот модуль):** явные контролы, которыми пользователь
 *    управляет сам. Каждый флаг ставит свой `data-a11y-*` атрибут на
 *    `document.documentElement` (источник истины для CSS-переопределений), а размер
 *    шрифта — CSS-переменную `--font-scale` через `style.setProperty` (единственный
 *    масштабирующий рычаг: `html { font-size: calc(100% * var(--font-scale)) }`).
 *
 * Состояние хранится единым JSON-объектом в localStorage под ключом `kikayoo:a11y`
 * (двоеточие — конвенция проекта, как `kikayoo:sound` в `sound.ts`; тема использует
 * дефис `kikayoo-theme` — это разные подсистемы). Применяется инлайн-скриптом в
 * `index.html` **до первого рендера** (без вспышки), а здесь — повторно при рендере
 * панели и при каждом изменении (для синхронизации DOM + сохранения).
 *
 * По образцу `ThemeToggle.tsx` (`applyTheme`/`THEME_KEY`): чистые функции +
 * безопасный парсинг с fallback на дефолты (защита от частичного/битого JSON, R5).
 */

/** Ключ localStorage, под которым сохраняется весь объект настроек a11y. */
export const A11Y_KEY = 'kikayoo:a11y';

/**
 * Допустимые значения масштаба шрифта. Соответствуют степам панели A−/A/A+/A++.
 * Числа (а не строки-ключи), т.к. применяются напрямую в `--font-scale` через
 * `style.setProperty` — без промежуточных CSS-блоков `[data-a11y-fontsize='...']`.
 * Сортировка по возрастанию для корректного рендера степпера.
 */
export const FONT_SIZE_STEPS = [0.9, 1.0, 1.15, 1.3] as const;

/** Человекочитаемые подписи степов шрифта (для кнопок-радио в `A11yPanel`). */
export const FONT_STEP_LABELS: Readonly<Record<number, string>> = {
  0.9: 'A−',
  1.0: 'A',
  1.15: 'A+',
  1.3: 'A++',
};

/**
 * Полное состояние панели accessibility. Все поля независимы (гранулярность) и
 * комбинируются пользователем произвольно.
 */
export interface A11ySettings {
  /** Масштаб шрифта (одно из FONT_SIZE_STEPS); управляет `--font-scale` на <html>. */
  fontSize: number;
  /** Высокая контрастность → `data-a11y-contrast="high"` (сплошные чёрный/белый). */
  highContrast: boolean;
  /** Уменьшить анимацию → `data-a11y-motion="reduce"` (отключение transition/animation). */
  reduceMotion: boolean;
  /** Шрифт для дислексии → `data-a11y-dyslexia="on"` (web-safe, без тяжёлых шрифтов). */
  dyslexiaFont: boolean;
  /** Подчёркивать ссылки → `data-a11y-underline="on"`. */
  underlineLinks: boolean;
}

/** Значения по умолчанию (соответствуют «выключенной» панели). */
export const DEFAULT_A11Y_SETTINGS: A11ySettings = {
  fontSize: 1.0,
  highContrast: false,
  reduceMotion: false,
  dyslexiaFont: false,
  underlineLinks: false,
};

/**
 * Проверяет, что значение — конечное число из набора допустимых степов. Сравнение
 * с допуском 1e-6 защищает от float-погрешностей при JSON round-trip (0.9 в double
 * неточно представимо, но `Math.abs(0.9 - JSON.parse('0.9')) === 0`).
 */
export function isFontSizeStep(value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  return FONT_SIZE_STEPS.some((step) => Math.abs(step - value) < 1e-6);
}

/**
 * Читает настройки из localStorage, безопасно мержит с дефолтами. Защита от:
 *  - отсутствия ключа (новый пользователь) → дефолты;
 *  - битого JSON (ручная правка в DevTools / приватный режим) → дефолты;
 *  - частичного объекта (старая версия без новых полей) → дефолт для отсутствующих;
 *  - невалидного `fontSize` (вне степов) → дефолт 1.0.
 */
export function loadA11y(): A11ySettings {
  if (typeof window === 'undefined') return { ...DEFAULT_A11Y_SETTINGS };
  try {
    const raw = window.localStorage.getItem(A11Y_KEY);
    if (!raw) return { ...DEFAULT_A11Y_SETTINGS };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_A11Y_SETTINGS };
    const obj = parsed as Record<string, unknown>;
    return {
      fontSize: isFontSizeStep(obj.fontSize) ? obj.fontSize : DEFAULT_A11Y_SETTINGS.fontSize,
      highContrast: typeof obj.highContrast === 'boolean' ? obj.highContrast : false,
      reduceMotion: typeof obj.reduceMotion === 'boolean' ? obj.reduceMotion : false,
      dyslexiaFont: typeof obj.dyslexiaFont === 'boolean' ? obj.dyslexiaFont : false,
      underlineLinks: typeof obj.underlineLinks === 'boolean' ? obj.underlineLinks : false,
    };
  } catch {
    return { ...DEFAULT_A11Y_SETTINGS };
  }
}

/**
 * Сохраняет настройки в localStorage. Обёрнуто в try/catch: приватный режим /
 * превышение квоты не должны ронять UI — атрибуты всё равно применятся на сессию
 * через `applyA11yAttrs`.
 */
export function saveA11y(state: A11ySettings): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(A11Y_KEY, JSON.stringify(state));
  } catch {
    /* localStorage недоступен — настройки живут только в текущей DOM-сессии. */
  }
}

/** Ставит/снимает булев `data-*` атрибут на элементе (значение — константа при вкл). */
function setToggleAttr(el: HTMLElement, name: string, onValue: string, on: boolean): void {
  if (on) {
    el.setAttribute(name, onValue);
  } else {
    el.removeAttribute(name);
  }
}

/**
 * Применяет состояние к `document.documentElement`: ставит `--font-scale` и нужные
 * `data-a11y-*` атрибуты, снимает выключенные, и сохраняет в localStorage.
 *
 * `--font-scale` — единственный рычаг масштабирования шрифта; ставится через
 * `style.setProperty` (inline-стиль на <html>), чтобы CSS мог его читать в
 * `html { font-size: calc(100% * var(--font-scale)) }`. CSS-переменная читается
 * через `var(--font-scale, 1)` с дефолтом 1 на случай, если скрипт не отработал.
 */
export function applyA11yAttrs(state: A11ySettings): void {
  if (typeof document === 'undefined') return;
  const doc = document.documentElement;
  doc.style.setProperty('--font-scale', String(state.fontSize));
  setToggleAttr(doc, 'data-a11y-contrast', 'high', state.highContrast);
  setToggleAttr(doc, 'data-a11y-motion', 'reduce', state.reduceMotion);
  setToggleAttr(doc, 'data-a11y-dyslexia', 'on', state.dyslexiaFont);
  setToggleAttr(doc, 'data-a11y-underline', 'on', state.underlineLinks);
  saveA11y(state);
}

/**
 * Сбрасывает все настройки к дефолтам: применяет дефолтное состояние к DOM (снимает
 * все `data-a11y-*`, возвращает `--font-scale` = 1) и сохраняет в localStorage.
 * Возвращает дефолтный объект, чтобы вызывающий код (`A11yPanel`) синхронизировал
 * свой React-state в одном вызове.
 */
export function resetA11y(): A11ySettings {
  const next: A11ySettings = { ...DEFAULT_A11Y_SETTINGS };
  applyA11yAttrs(next);
  return next;
}
